/**
 * Segment condition formatting utilities
 * Formats segment conditions to match the display format used in the segment builder UI (~/code/ui)
 */

import { isEmptyString } from '@/utils/string';
import { tryDecodeBase64 } from './formatting';

// =============================================================================
// Condition Type Mappings
// =============================================================================

/**
 * Condition type labels
 * Maps backend condition type values to user-friendly display names
 */
export const ConditionTypeLabels: Record<string, string> = {
  attribute: 'Person attribute',
  event_attribute: 'Event attribute',
  foreign_event_attribute: 'Foreign event attribute',
  object_attribute: 'Object attribute',
  relationship_attribute: 'Relationship attribute',
  segments_negatable: 'Segment',
  segment: 'Segment',
  event: 'Event',
  message: 'Message interaction',
  device_attribute: 'Device attribute',
  collection_attribute: 'Collection attribute',
  inbound: 'Inbound message',
  inbound_attribute: 'Inbound attribute',
  form: 'Form submission',
  pageview: 'Page view',
  screenview: 'Screen view',
  action: 'Action',
  email: 'Email',
  opt_out: 'Opt out',
  refinement: 'Refinement',
  related: 'Related object',
  relationship: 'Relationship',
  webhook_attribute: 'Webhook attribute',
  foreign_event: 'Foreign event',
};

// =============================================================================
// Operator Mappings
// =============================================================================

/**
 * Operator labels for conditions
 * Maps backend operator values to user-friendly display names
 * Matches the labels used in the segment builder UI (~/code/ui/app/utils/conditions-operators.ts)
 */
export const OperatorLabels: Record<string, string> = {
  eq: 'is',
  '=': 'is', // Alternative format
  equals: 'is', // UI format
  '!eq': 'is not',
  '!=': 'is not', // Alternative format
  not_equals: 'is not', // UI format
  gt: 'is greater than',
  '>': 'is greater than', // Alternative format
  lt: 'is less than',
  '<': 'is less than', // Alternative format
  bw: 'is between',
  exists: 'exists',
  '!exists': 'does not exist',
  contains: 'contains',
  '!contains': 'does not contain',
  timestamp: 'is a timestamp',
  '!timestamp': 'is not a timestamp',
  timestamp_gt: 'is after', // relative
  timestamp_lt: 'is before', // relative
  timestamp_gta: 'is after', // absolute
  timestamp_lta: 'is before', // absolute
  timestamp_bw: 'is between',
  // UI format operators
  after_relative_timestamp: 'is after',
  before_relative_timestamp: 'is before',
  after_absolute_timestamp: 'is after',
  before_absolute_timestamp: 'is before',
  between_absolute_timestamps: 'is between',
  email_valid: 'is a valid email',
  '!email_valid': 'is not a valid email',
  contained_in_array: 'is contained in',
  '!contained_in_array': 'is not contained in',
};

/**
 * Inverse operator labels for conditions
 * Used when the condition has inverse: true but the operator is not prefixed with !
 * Maps regular operators to their inverse display labels
 */
export const InverseOperatorLabels: Record<string, string> = {
  eq: 'is not',
  '=': 'is not',
  gt: 'is not greater than',
  '>': 'is not greater than',
  lt: 'is not less than',
  '<': 'is not less than',
  bw: 'is not between',
  exists: 'does not exist',
  contains: 'does not contain',
  timestamp: 'is not a timestamp',
  timestamp_gt: 'is not after',
  timestamp_lt: 'is not before',
  timestamp_gta: 'is not after',
  timestamp_lta: 'is not before',
  timestamp_bw: 'is not between',
  after_relative_timestamp: 'is not after',
  before_relative_timestamp: 'is not before',
  after_absolute_timestamp: 'is not after',
  before_absolute_timestamp: 'is not before',
  between_absolute_timestamps: 'is not between',
  email_valid: 'is not a valid email',
  contained_in_array: 'is not contained in',
};

/**
 * Get the operator label, considering the inverse flag
 */
export function getOperatorLabel(operator: string | undefined, inverse?: boolean): string | undefined {
  if (operator == null) return undefined;

  // If inverse is true and we have an inverse label, use it
  if (inverse === true && operator in InverseOperatorLabels) {
    return InverseOperatorLabels[operator];
  }

  // Otherwise use the regular label
  return OperatorLabels[operator] ?? operator;
}

/**
 * Time direction labels for relative timestamp operators.
 * Maps backend timeDirection values to user-friendly display names.
 * Used with timestamp_gt and timestamp_lt operators.
 */
export const TimeDirectionLabels: Record<string, string> = {
  ago: 'ago',
  from_now: 'from now',
  fromNow: 'from now', // Alternative camelCase format
};

// =============================================================================
// Activity/Event Type Mappings (Message Events)
// =============================================================================

/**
 * Activity log type / Event type labels.
 * Maps backend event_type/eventType values to user-friendly display names.
 * Used for message conditions (sent, opened, clicked, bounced, etc.)
 * Source: ~/code/ui/app/utils/activity-log-types.ts
 */
export const EventTypeLabels: Record<string, string> = {
  // Anonymous and Profile events
  anon_merge: 'Anonymous Data Merged',
  profile_create: 'Person Created',
  profile_delete: 'Person Deleted',
  profile_merge: 'People Merged',

  // Attribute changes
  attribute_change: 'Attribute Change',
  failed_attribute_change: 'Failed Attribute Change',
  device_change: 'Device Change',

  // Object events
  '_o:*:attribute_change': 'Object Attribute Change',
  '_o:*:create': 'Object Created',
  '_o:*:delete': 'Object Deleted',
  '_o:*:failed_attribute_change': 'Object Failed Attribute Change',
  '_o:*:add_relationship': 'Relationship Created (to Person)',
  '_o:*:delete_relationship': 'Relationship Deleted (to Person)',

  // Relationship events
  '_r:*:attribute_change': 'Relationship Attribute Change',
  '_r:*:failed_attribute_change': 'Relationship Failed Attribute Change',
  add_relationship: 'Relationship Created (to Object)',
  delete_relationship: 'Relationship Deleted (to Object)',

  // Generic event
  event: 'Event',
  failed_event: 'Failed Event',

  // Page/Screen views
  page: 'Page View',
  screen: 'Screen View',

  // Segment events
  profile_segment_membership_change: 'Segment Membership Change',
  segment_build: 'Build Segment',

  // Webhook events
  webhook_event: 'Webhook Arrived',
  inbound: 'Inbound Message',

  // Batch events
  failed_batch_update: 'Failed Batch Update',
  skipped_update: 'Skipped Update',
  failed_query_collection: 'Failed Collection Query',
  failed_object_journeys: 'Failed Journeys for Object/Relationship Campaign',

  // ===== Email events =====
  attempted_email: 'Attempted To Send Email',
  sent_email: 'Sent Email',
  delivered_email: 'Delivered Email',
  opened_email: 'Opened Email',
  clicked_email: 'Clicked Email',
  bounced_email: 'Bounced Email',
  spammed_email: 'Spammed Email',
  unsubscribed_email: 'Unsubscribed Email',
  topic_unsubscribed_email: 'Topics Unsubscribed Email',
  converted_email: 'Converted Email',
  deferred_email: 'Deferred Email',
  drafted_email: 'Drafted Email',
  dropped_email: 'Suppressed Email',
  failed_email: 'Failed to Send Email',
  undeliverable_email: 'Undeliverable Email',

  // ===== Push notification events =====
  attempted_push: 'Attempted To Send Push Notification',
  sent_push: 'Sent Push Notification',
  delivered_push: 'Delivered Push Notification',
  opened_push: 'Opened Push Notification',
  clicked_push: 'Clicked Push Notification',
  bounced_push: 'Bounced Push Notification',
  deferred_push: 'Deferred Push Notification',
  drafted_push: 'Drafted Push Notification',
  failed_push: 'Failed to Send Push Notification',
  undeliverable_push: 'Undeliverable Push Notification',

  // ===== SMS (Twilio) events =====
  attempted_twilio: 'Attempted To Send SMS',
  sent_twilio: 'Sent SMS',
  delivered_twilio: 'Delivered SMS',
  clicked_twilio: 'Clicked SMS',
  bounced_twilio: 'Bounced SMS',
  suppressed_twilio: 'Suppressed SMS',
  replied_twilio: 'Replied to SMS',
  deferred_twilio: 'Deferred SMS',
  drafted_twilio: 'Drafted SMS',
  failed_twilio: 'Failed to Send SMS',
  undeliverable_twilio: 'Undeliverable SMS',

  // ===== In-App message events =====
  attempted_in_app: 'Attempted To Send In-App Message',
  sent_in_app: 'Sent In-App Message',
  opened_in_app: 'Opened In-App Message',
  clicked_in_app: 'Clicked In-App Message',
  converted_in_app: 'Converted In-App Message',
  deferred_in_app: 'Deferred In-App Message',
  drafted_in_app: 'Drafted In-App Message',
  failed_in_app: 'Failed to Send In-App Message',
  undeliverable_in_app: 'Undeliverable In-App Message',

  // ===== Slack events =====
  attempted_slack: 'Attempted To Send Slack Message',
  sent_slack: 'Sent Slack Message',
  converted_slack: 'Converted Slack Message',
  deferred_slack: 'Deferred Slack Message',
  drafted_slack: 'Drafted Slack Message',
  failed_slack: 'Failed to Send Slack Message',
  undeliverable_slack: 'Undeliverable Slack Message',

  // ===== LINE events =====
  attempted_line: 'Attempted To Send LINE Message',
  sent_line: 'Sent LINE Message',
  clicked_line: 'Clicked LINE Message',
  converted_line: 'Converted LINE Message',
  failed_line: 'Failed to Send LINE Message',

  // ===== Webhook events =====
  attempted_webhook: 'Attempted To Send Webhook',
  sent_webhook: 'Sent Webhook',
  clicked_webhook: 'Clicked Webhook',
  converted_webhook: 'Converted Webhook',
  deferred_webhook: 'Deferred Webhook',
  drafted_webhook: 'Drafted Webhook',
  failed_webhook: 'Failed to Send Webhook',
  undeliverable_webhook: 'Undeliverable Webhook',

  // ===== Generic Action events =====
  attempted_action: 'Attempted to Send Action',
  sent_action: 'Sent Action',
  delivered_action: 'Delivered Action',
  opened_action: 'Opened Action',
  clicked_action: 'Clicked Action',
  bounced_action: 'Bounced Action',
  converted_action: 'Converted Action',
  deferred_action: 'Deferred Action',
  drafted_action: 'Drafted Action',
  dropped_action: 'Suppressed Action',
  failed_action: 'Failed to Send Action',
  unsubscribed_action: 'Unsubscribed Action',
  undeliverable_action: 'Undeliverable Action',
  replied_action: 'Replied to Action',

  // ===== Content events =====
  viewed_content: 'Viewed Content',
  clicked_content: 'Clicked Content',
  converted_content: 'Converted Content',

  // ===== LLM events =====
  failed_llm_action: 'Failed LLM',
};

/**
 * Check if an operator is a relative timestamp operator.
 * Relative operators use timestampValue and timeDirection fields.
 */
export function isRelativeTimestampOperator(operator: string): boolean {
  return operator === 'timestamp_gt' || operator === 'timestamp_lt';
}

/**
 * Check if an operator is an absolute timestamp operator.
 * Absolute operators use a date value directly.
 */
export function isAbsoluteTimestampOperator(operator: string): boolean {
  return operator === 'timestamp_gta' || operator === 'timestamp_lta';
}

/**
 * Check if an operator is a unary operator (no value required).
 */
export function isUnaryOperator(operator: string): boolean {
  return ['exists', '!exists', 'timestamp', '!timestamp', 'email_valid', '!email_valid'].includes(operator);
}

// =============================================================================
// Condition/Filter Formatting Functions
// =============================================================================

// =============================================================================
// Filter Type Definitions
// =============================================================================

/**
 * Base filter structure with optional logical operators
 */
interface BaseFilter {
  and?: FilterCondition[] | { children: FilterCondition[] };
  or?: FilterCondition[] | { children: FilterCondition[] };
  not?: FilterCondition;
}

/**
 * Segment reference filter
 */
interface SegmentFilter extends BaseFilter {
  segment?: { id: number; inverse?: boolean };
  segment_id?: number;
  condition_id?: number;
  inverse?: boolean;
}

/**
 * Event name filter
 */
interface EventNameFilter extends BaseFilter {
  event_name?: { name?: string; etype?: string };
}

/**
 * Attribute filter with operator and value
 */
interface AttributeFilter extends BaseFilter {
  type?: string;
  field?: string;
  attribute?: string;
  name?: string;
  operator?: string;
  value?: unknown;
  value2?: unknown;
  inverse?: boolean;
}

/**
 * Union type for all filter conditions
 */
type FilterCondition = BaseFilter | SegmentFilter | EventNameFilter | AttributeFilter | Record<string, unknown>;

// =============================================================================
// Filter Formatting Sub-Functions
// =============================================================================

/**
 * Format a logical AND operator with its children.
 * Handles both array format { and: [...] } and object format { and: { children: [...] } }
 *
 * @param andValue - The value of the 'and' property
 * @returns Formatted string or null if not applicable
 */
function formatAndOperator(andValue: unknown): string | null {
  // Format 1: { and: [...] } - array directly
  if (Array.isArray(andValue)) {
    const formatted = (andValue as FilterCondition[]).map(formatFilter);
    return formatted.length > 1 ? `(${formatted.join(' AND ')})` : formatted[0] ?? 'Empty';
  }

  // Format 2: { and: { children: [...] } } - object with children array
  if (typeof andValue === 'object' && andValue != null) {
    const andObject = andValue as Record<string, unknown>;
    if ('children' in andObject && Array.isArray(andObject.children)) {
      const formatted = (andObject.children as FilterCondition[]).map(formatFilter);
      return formatted.length > 1 ? `(${formatted.join(' AND ')})` : formatted[0] ?? 'Empty';
    }
  }

  return null;
}

/**
 * Format a logical OR operator with its children.
 * Handles both array format { or: [...] } and object format { or: { children: [...] } }
 *
 * @param orValue - The value of the 'or' property
 * @returns Formatted string or null if not applicable
 */
function formatOrOperator(orValue: unknown): string | null {
  // Format 1: { or: [...] } - array directly
  if (Array.isArray(orValue)) {
    const formatted = (orValue as FilterCondition[]).map(formatFilter);
    return formatted.length > 1 ? `(${formatted.join(' OR ')})` : formatted[0] ?? 'Empty';
  }

  // Format 2: { or: { children: [...] } } - object with children array
  if (typeof orValue === 'object' && orValue != null) {
    const orObject = orValue as Record<string, unknown>;
    if ('children' in orObject && Array.isArray(orObject.children)) {
      const formatted = (orObject.children as FilterCondition[]).map(formatFilter);
      return formatted.length > 1 ? `(${formatted.join(' OR ')})` : formatted[0] ?? 'Empty';
    }
  }

  return null;
}

/**
 * Format a segment reference filter.
 * Handles { segment: { id, inverse } } format.
 *
 * @param filter - The filter object
 * @returns Formatted string or null if not a segment filter
 */
function formatSegmentObjectFilter(filter: Record<string, unknown>): string | null {
  if (!('segment' in filter) || typeof filter.segment !== 'object' || filter.segment == null) {
    return null;
  }

  const seg = filter.segment as Record<string, unknown>;
  const segmentId = seg.id;
  const inverse = seg.inverse === true;
  return inverse ? `NOT in segment #${segmentId}` : `In segment #${segmentId}`;
}

/**
 * Format a segment/condition ID reference filter.
 * Handles { segment_id, condition_id, inverse } format.
 *
 * @param filter - The filter object
 * @returns Formatted string or null if not applicable
 */
function formatSegmentIdFilter(filter: Record<string, unknown>): string | null {
  if (!('segment_id' in filter) && !('condition_id' in filter)) {
    return null;
  }

  const segmentId = (filter.segment_id as number) ?? 0;
  const conditionId = (filter.condition_id as number) ?? 0;
  const inverse = filter.inverse === true;

  // If segment_id > 0, this references a Segment
  if (segmentId > 0) {
    return inverse ? `NOT in segment #${segmentId}` : `In segment #${segmentId}`;
  }

  // If condition_id > 0, this references a Condition
  if (conditionId > 0) {
    return inverse ? `NOT matching condition #${conditionId}` : `Condition #${conditionId}`;
  }

  // Both are 0 - check for nested and/or operators
  if ('and' in filter) {
    const result = formatAndOperator(filter.and);
    if (!isEmptyString(result)) return result;
  }
  if ('or' in filter) {
    const result = formatOrOperator(filter.or);
    if (!isEmptyString(result)) return result;
  }

  // Fallback for root node with both IDs = 0
  return '';
}

/**
 * Format an event name filter.
 * Handles { event_name: { name, etype } } format.
 *
 * @param filter - The filter object
 * @returns Formatted string or null if not an event name filter
 */
function formatEventNameFilter(filter: Record<string, unknown>): string | null {
  if (!('event_name' in filter) || typeof filter.event_name !== 'object' || filter.event_name == null) {
    return null;
  }

  const eventName = filter.event_name as Record<string, unknown>;
  const name = eventName.name as string | undefined;
  const etype = eventName.etype as string | undefined;

  if (name == null) {
    return null;
  }

  // Try to decode base64 event name
  const decodedName = tryDecodeBase64(name);
  return !isEmptyString(etype) ? `Event "${decodedName}" (${etype})` : `Event "${decodedName}"`;
}

/**
 * Format an attribute filter with operator and value.
 * Handles { field/attribute/name, operator, value, value2, inverse } format.
 *
 * @param filter - The filter object
 * @returns Formatted string or null if not an attribute filter
 */
function formatAttributeFilter(filter: Record<string, unknown>): string | null {
  const type = filter.type as string | undefined;
  const field = filter.field as string | undefined;
  const attribute = filter.attribute as string | undefined;
  const name = filter.name as string | undefined;
  const operator = filter.operator as string | undefined;
  const value = filter.value;
  const value2 = filter.value2;
  const inverse = filter.inverse === true;

  // Determine the field/attribute name
  const fieldName = field ?? attribute ?? name;

  if (fieldName != null && operator != null) {
    let operatorLabel = OperatorLabels[operator] ?? operator;

    // Handle inverse by prepending "NOT" or using negated operator
    if (inverse && !operator.startsWith('!')) {
      operatorLabel = `NOT ${operatorLabel}`;
    }

    // Unary operators (no value needed)
    if (isUnaryOperator(operator)) {
      return `${fieldName} ${operatorLabel}`;
    }

    // Two-value operators (between)
    if (value != null && value2 != null) {
      return `${fieldName} ${operatorLabel} "${value}" and "${value2}"`;
    }

    // Standard binary operator with value
    if (value != null) {
      return `${fieldName} ${operatorLabel} "${value}"`;
    }

    return `${fieldName} ${operatorLabel}`;
  }

  // Handle attribute-only filter (just checking existence)
  if (fieldName != null) {
    return fieldName;
  }

  // Handle type label for condition type
  if (type != null) {
    const typeLabel = ConditionTypeLabels[type] ?? type;
    return typeLabel;
  }

  return null;
}

// =============================================================================
// Main Filter Formatting Function
// =============================================================================

/**
 * Format a single filter object for display.
 * Handles various filter types: attribute filters, segment filters, event filters, etc.
 *
 * @param filter - The raw filter object
 * @returns Human-readable filter description
 */
export function formatFilter(filter: FilterCondition): string {
  const filterObject = filter as Record<string, unknown>;
  // 1. Handle logical operators (and/or/not)
  if ('and' in filterObject) {
    const result = formatAndOperator(filterObject.and);
    if (result != null) return result;
  }

  if ('or' in filterObject) {
    const result = formatOrOperator(filterObject.or);
    if (result != null) return result;
  }

  if ('not' in filterObject) {
    const innerFilter = filterObject.not as FilterCondition;
    return `NOT (${formatFilter(innerFilter)})`;
  }

  // 2. Handle segment object filter: { segment: { id, inverse } }
  const segmentResult = formatSegmentObjectFilter(filterObject);
  if (segmentResult != null) return segmentResult;

  // 3. Handle segment/condition ID filter: { segment_id, condition_id, inverse }
  const segmentIdResult = formatSegmentIdFilter(filterObject);
  if (segmentIdResult != null) return segmentIdResult;

  // 4. Handle event name filter: { event_name: { name, etype } }
  const eventResult = formatEventNameFilter(filterObject);
  if (eventResult != null) return eventResult;

  // 5. Handle attribute filter: { field/attribute/name, operator, value }
  const attributeResult = formatAttributeFilter(filterObject);
  if (attributeResult != null) return attributeResult;

  // 6. Fallback: format as simplified string
  return formatFilterAsSimpleString(filterObject);
}

/**
 * Format a filter object as a simplified string (non-JSON).
 * Shows key-value pairs in a readable format.
 */
function formatFilterAsSimpleString(filter: Record<string, unknown>): string {
  const parts: string[] = [];

  for (const [key, value] of Object.entries(filter)) {
    if (value == null) continue;

    if (typeof value === 'object' && !Array.isArray(value)) {
      // Recursively format nested objects
      const nested = formatFilterAsSimpleString(value as Record<string, unknown>);
      parts.push(`${key}: ${nested}`);
    } else if (Array.isArray(value)) {
      parts.push(`${key}: [${value.length} items]`);
    } else {
      parts.push(`${key}: ${value}`);
    }
  }

  return parts.join(', ');
}

/**
 * Format a condition filter for display (legacy function name).
 * Converts raw condition objects to human-readable strings.
 *
 * @param condition - The raw condition object
 * @returns Human-readable condition description
 */
export function formatCondition(condition: Record<string, unknown>): string {
  return formatFilter(condition);
}

/**
 * Format an array of conditions/filters for display.
 *
 * @param conditions - Array of condition objects
 * @param operator - The logical operator ('and' or 'or')
 * @returns Human-readable conditions description
 */
export function formatConditions(conditions: Record<string, unknown>[], operator: 'and' | 'or' = 'and'): string {
  if (!Array.isArray(conditions) || conditions.length === 0) {
    return 'No conditions';
  }

  const formatted = conditions.map(formatFilter);
  const connector = operator === 'and' ? ' AND ' : ' OR ';
  return formatted.join(connector);
}

/**
 * Format filters from various audit log fields.
 * Handles different filter structures: { and: [...] }, { or: [...] }, { not: {...} }, or array format.
 *
 * @param filtersValue - The filters value from the audit log
 * @returns Human-readable filters description
 */
export function formatFilters(filtersValue: unknown): string {
  if (filtersValue == null) {
    return 'No filters';
  }

  // Empty array
  if (Array.isArray(filtersValue) && filtersValue.length === 0) {
    return 'No filters';
  }

  // Handle filters object structure: { and: [...] }, { or: [...] }, { not: {...} }
  if (typeof filtersValue === 'object' && !Array.isArray(filtersValue)) {
    const filtersObject = filtersValue as Record<string, unknown>;

    // Empty object
    if (Object.keys(filtersObject).length === 0) {
      return 'No filters';
    }

    return formatFilter(filtersObject);
  }

  // If it's an array directly, assume 'and' operator
  if (Array.isArray(filtersValue)) {
    return formatConditions(filtersValue as Record<string, unknown>[], 'and');
  }

  // Fallback to string representation
  return String(filtersValue);
}

/**
 * Format segment conditions from the conditions field.
 * Handles different condition structures: { and: [...] }, { or: [...] }, or array format.
 *
 * @param conditionsValue - The conditions value from the segment audit log
 * @returns Human-readable conditions description
 */
export function formatSegmentConditions(conditionsValue: unknown): string {
  return formatFilters(conditionsValue);
}
